import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { FramedImage } from "@/components/FramedImage";
import { Reveal } from "@/components/Reveal";
import type { Photo } from "@/lib/photos";

type CarouselProps = {
  photos: Photo[];
  labels: string[];
  onOpen: (slug: string) => void;
};

const SIZES = "(min-width: 640px) 30vw, 78vw";

/**
 * Horizontally swipeable snap carousel on small screens, plain 3-up grid from
 * `sm` up where the frames align perfectly. Dot indicators are mobile-only.
 */
export function Carousel({ photos, labels, onOpen }: CarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);

  const syncIndex = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const children = Array.from(track.children) as HTMLElement[];
    if (!children.length) return;

    const trackLeft = track.getBoundingClientRect().left;
    let closest = 0;
    let closestDist = Infinity;

    children.forEach((child, i) => {
      const d = Math.abs(child.getBoundingClientRect().left - trackLeft);
      if (d < closestDist) {
        closestDist = d;
        closest = i;
      }
    });

    setIndex(closest);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    track.addEventListener("scroll", syncIndex, { passive: true });
    return () => track.removeEventListener("scroll", syncIndex);
  }, [syncIndex]);

  const scrollTo = (i: number) => {
    const track = trackRef.current;
    const child = track?.children[i] as HTMLElement | undefined;
    if (!track || !child) return;
    track.scrollTo({ left: child.offsetLeft - track.offsetLeft, behavior: "smooth" });
  };

  return (
    <div>
      <div
        ref={trackRef}
        className="-mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain px-6 pb-2 no-scrollbar sm:mx-0 sm:grid sm:grid-cols-3 sm:gap-6 sm:overflow-visible sm:px-0 lg:gap-10"
      >
        {photos.map((photo, i) => (
          <div
            key={photo.slug}
            className="w-[78vw] shrink-0 snap-center sm:w-auto sm:shrink"
          >
            <Reveal variant="blur" delay={i * 110} duration={1200}>
              <FramedImage
                photo={photo}
                sizes={SIZES}
                index={`0${i + 1}`}
                label={labels[i]}
                onOpen={() => onOpen(photo.slug)}
              />
            </Reveal>
          </div>
        ))}
      </div>

      <div className="mt-8 flex items-center justify-center gap-2 sm:hidden">
        {photos.map((photo, i) => (
          <button
            key={photo.slug}
            type="button"
            onClick={() => scrollTo(i)}
            aria-label={`Go to ${labels[i]}`}
            aria-current={i === index}
            className={cn(
              "h-1.5 cursor-pointer rounded-full transition-all duration-500",
              i === index ? "w-7 bg-gold" : "w-1.5 bg-border",
            )}
          />
        ))}
      </div>
    </div>
  );
}
